import { Fragment, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button, Select, Table, TableHead, TableHeader } from '@/design-system';
import { cn } from '@/design-system/utils/cn';
import { useErrorToast } from '@/core/errors/useErrorToast';
import { useMigrationJobsQuery } from '../hooks/useMigrationJobsQuery';
import type { MigrationJobListItem, MigrationJobStatus } from '../types';
import { formatDateTime } from '../utils/migration-format';

const STATUS_LABEL: Record<MigrationJobStatus, string> = {
  pending: 'Na fila',
  fetching: 'Buscando',
  syncing: 'Sincronizando',
  completed: 'Concluído',
  failed: 'Falhou',
};

const STATUS_CLASS: Record<MigrationJobStatus, string> = {
  pending: 'bg-slate-100 text-slate-700',
  fetching: 'bg-sky-100 text-sky-800',
  syncing: 'bg-amber-100 text-amber-800',
  completed: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-800',
};

function ProgressCell({ job }: { job: MigrationJobListItem }) {
  if (job.status === 'completed') {
    return (
      <div className="space-y-1">
        <div className="h-2 overflow-hidden rounded-full bg-emerald-100">
          <div className="h-full w-full rounded-full bg-emerald-500" />
        </div>
        <div className="text-[11px] text-gray-500">100%</div>
      </div>
    );
  }

  if (job.progressPercent == null) {
    return (
      <div className="space-y-1">
        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full w-1/3 animate-pulse rounded-full bg-slate-300" />
        </div>
        <div className="text-[11px] text-gray-500">
          {job.status === 'pending' || job.status === 'fetching'
            ? 'Aguardando total…'
            : '—'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            job.status === 'failed' ? 'bg-red-400' : 'bg-[#0084FF]'
          )}
          style={{ width: `${job.progressPercent}%` }}
        />
      </div>
      <div className="text-[11px] text-gray-500">
        {job.progressPercent}% · {job.processedChats}/{job.totalChats} chats
      </div>
    </div>
  );
}

export function MigrationJobsPanel() {
  const onErrorToast = useErrorToast();
  const [statusFilter, setStatusFilter] = useState<MigrationJobStatus | ''>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filters = useMemo(
    () => ({
      limit: 50,
      status: statusFilter || undefined,
    }),
    [statusFilter]
  );

  const { data, isLoading, isFetching, isError, error, refetch } =
    useMigrationJobsQuery(filters);

  useEffect(() => {
    if (isError && error) onErrorToast(error, 'Erro ao carregar jobs de sincronização');
  }, [isError, error, onErrorToast]);

  const jobs = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Jobs de sincronização</h3>
          <p className="mt-1 text-sm text-gray-500">
            Status por canal (telefone). Atualiza automaticamente enquanto houver jobs em
            andamento.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as MigrationJobStatus | '')}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm"
          >
            <option value="">Todos os status</option>
            {(Object.keys(STATUS_LABEL) as MigrationJobStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            <RefreshCw size={14} className={cn(isFetching && 'animate-spin')} />
            Atualizar
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <Table>
          <TableHeader>
            <tr>
              <TableHead>Telefone / canal</TableHead>
              <TableHead>Empresa</TableHead>
              <TableHead>Executado por</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="min-w-[140px]">Progresso</TableHead>
              <TableHead>Mensagens</TableHead>
              <TableHead>Erros</TableHead>
              <TableHead>Atualizado</TableHead>
            </tr>
          </TableHeader>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500">
                  Carregando jobs…
                </td>
              </tr>
            ) : jobs.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500">
                  Nenhum job encontrado.
                </td>
              </tr>
            ) : (
              jobs.map((job) => {
                const expanded = expandedId === job.jobId;
                return (
                  <Fragment key={job.jobId}>
                    <tr
                      className={cn(
                        'cursor-pointer border-b border-gray-100 hover:bg-slate-50',
                        job.status === 'failed' && 'bg-red-50/40'
                      )}
                      onClick={() =>
                        setExpandedId((current) =>
                          current === job.jobId ? null : job.jobId
                        )
                      }
                    >
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{job.displayPhone}</div>
                        <div className="text-xs text-gray-500">{job.channelName}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{job.companyName}</td>
                      <td className="px-6 py-4">
                        {job.triggeredByStaffName || job.triggeredByStaffEmail ? (
                          <>
                            <div className="text-sm text-gray-700">
                              {job.triggeredByStaffName ?? '—'}
                            </div>
                            {job.triggeredByStaffEmail ? (
                              <div className="text-xs text-gray-500">
                                {job.triggeredByStaffEmail}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={cn(
                            'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                            STATUS_CLASS[job.status]
                          )}
                        >
                          {STATUS_LABEL[job.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <ProgressCell job={job} />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {job.totalMessagesInserted}
                        {job.totalMessagesSkipped > 0 ? (
                          <span className="text-xs text-gray-400">
                            {' '}
                            (+{job.totalMessagesSkipped} skip)
                          </span>
                        ) : null}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{job.errors}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {formatDateTime(job.updatedAt)}
                      </td>
                    </tr>
                    {expanded ? (
                      <tr className="border-b border-gray-100">
                        <td colSpan={8} className="bg-slate-50 px-6 py-3 text-sm text-gray-600">
                          <div className="space-y-1 py-1">
                            <div>
                              <span className="font-medium text-gray-700">Job ID:</span>{' '}
                              <code className="text-xs">{job.jobId}</code>
                            </div>
                            {job.errorMessage ? (
                              <div className="text-red-700">
                                <span className="font-medium">Erro:</span> {job.errorMessage}
                              </div>
                            ) : (
                              <div className="text-gray-500">Sem mensagem de erro.</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}
